import React, { useRef, useState } from "react";
import {
	setRecaptchaToken,
	startLoginRequest,
	verifyOtpRequest,
} from "../redux/slices/auth.slice";
import { validateEmail } from "../utils/common.utils";
import { RootState, useAppDispatch, useAppSelector } from "../redux/root";
import { updateSnackbarState } from "../redux/slices/common.slice";
import { snackBarType } from "../redux/types/common.types";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const useFormAuth = () => {
	const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
	const [resendTimer, setResendTimer] = useState(0);
	const [isResending, setIsResending] = useState(false);

	const formState = useAppSelector((state: RootState) => state.uploadForm);
	const dispatch = useAppDispatch();
	const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
	const { executeRecaptcha } = useGoogleReCaptcha();

	const setSnackbarMessage = (message: string) => {
		dispatch(
			updateSnackbarState({ message, type: snackBarType.info, isOpen: true })
		);
	};

	// ✅ Send OTP
	const handleSendOtp = async () => {
		if (!validateEmail(formState.email)) {
			setSnackbarMessage("Please enter a valid email address.");
			return;
		}
		if (!executeRecaptcha) {
			return;
		}

		let RecaptchaToken: string | null = null;

		for (let i = 0; i < 3; i++) {
			RecaptchaToken = await executeRecaptcha("resend_form");
			dispatch(setRecaptchaToken(RecaptchaToken));
			if (RecaptchaToken) {
				break;
			}
			await new Promise((res) => setTimeout(res, 500));
		}

		if (!RecaptchaToken) {
			return;
		}
		dispatch(startLoginRequest({ email: formState.email, RecaptchaToken }));
	};

	// ✅ Change OTP digits and auto-focus next
	const handleOtpChange = (index: number, value: string) => {
		if (!/^[0-9]?$/.test(value)) {
			return;
		} // allow only single digit

		const newOtp = [...otp];
		newOtp[index] = value;
		setOtp(newOtp);

		// Move to next box automatically
		if (value && index < 5) {
			otpInputRefs.current[index + 1]?.focus();
		}
		if (value && index === 5) {
			handleOtpVerify(newOtp.join(""));
		}
	};

	// ✅ Move focus backward on backspace
	const handleOtpKeyDown = (
		e: React.KeyboardEvent<HTMLInputElement>,
		index: number
	) => {
		if (e.key === "Backspace" && !otp[index] && index > 0) {
			otpInputRefs.current[index - 1]?.focus();
		}
	};

	// ✅ Paste handler (optional but great UX)
	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pasted = e.clipboardData.getData("text").trim();
		if (/^\d{6}$/.test(pasted)) {
			const newOtp = pasted.split("");
			setOtp(newOtp);
			newOtp.forEach((digit, i) => {
				if (otpInputRefs.current[i]) {
					otpInputRefs.current[i]!.value = digit;
				}
			});
			otpInputRefs.current[5]?.focus();
			dispatch(
				verifyOtpRequest({ otp: newOtp.join(""), email: formState.email })
			);
		}
	};

	// ✅ Verify OTP
	const handleOtpVerify = (otp: string) => {
		if (otp.length !== 6) {
			setSnackbarMessage("Please enter a valid 6-digit code.");
			return;
		}
		dispatch(verifyOtpRequest({ otp, email: formState.email }));
	};

	// resend otp

	const handleOTPResend = async () => {
		try {
			setIsResending(true);
			await handleSendOtp();
			setResendTimer(180); // 3-minute cooldown
		} catch (err) {
			console.error("Resend OTP failed:", err);
		} finally {
			setIsResending(false);
		}
	};

	const resetOtp = () => {
		setOtp(Array(6).fill(""));
	};

	return {
		handleSendOtp,
		handleOtpChange,
		handleOtpKeyDown,
		handleOtpVerify,
		handlePaste,
		handleOTPResend,
		resendTimer,
		setResendTimer,
		isResending,
		otpInputRefs,
		otp,
		resetOtp,
	};
};

export default useFormAuth;
